import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isBatchManaged } from "@/lib/playerBatchRegistry";

const SESSION_CACHE_KEY = "player_name_cache";
const CACHE_EXPIRY_MS = 6 * 60 * 60 * 1000; // 6 hours

interface CacheEntry {
  name: string;
  timestamp: number;
}

interface SessionCache {
  [uuid: string]: CacheEntry;
}

// Load cache from sessionStorage
const loadSessionCache = (): SessionCache => {
  try {
    const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!cached) return {};
    
    const data: SessionCache = JSON.parse(cached);
    const now = Date.now();
    
    const validCache: SessionCache = {};
    for (const [uuid, entry] of Object.entries(data)) {
      if (now - entry.timestamp < CACHE_EXPIRY_MS) {
        validCache[uuid] = entry;
      }
    }
    
    if (Object.keys(validCache).length !== Object.keys(data).length) {
      sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(validCache));
    }
    
    return validCache;
  } catch {
    return {};
  }
};

const saveToSessionCache = (uuid: string, name: string): void => {
  try {
    const cache = loadSessionCache();
    cache[uuid] = { name, timestamp: Date.now() };
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Storage quota exceeded
  }
};

const getFromSessionCache = (uuid: string): string | null => {
  const cache = loadSessionCache();
  const entry = cache[uuid];
  
  if (!entry) return null;
  
  if (Date.now() - entry.timestamp >= CACHE_EXPIRY_MS) {
    delete cache[uuid];
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cache));
    return null;
  }
  
  return entry.name;
};

// Fetch from Supabase backend cache with maybeSingle
const fetchFromBackend = async (uuid: string): Promise<{ name: string | null; fromCache: boolean } | null> => {
  try {
    const { data, error } = await (supabase as any)
      .from('api_gameusers')
      .select('name, updated_at')
      .eq('uuid', uuid)
      .maybeSingle();

    if (error || !data) return null;

    const updatedAt = new Date(data.updated_at);
    const sixHoursAgo = new Date(Date.now() - CACHE_EXPIRY_MS);

    if (updatedAt > sixHoursAgo && data.name) {
      return { name: data.name, fromCache: true };
    }

    return null;
  } catch {
    return null;
  }
};

const fetchFromEdgeFunction = async (uuid: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('gameuser-cache', {
      body: { uuid, size: 16 }
    });

    if (error) return null;
    return data?.name || null;
  } catch {
    return null;
  }
};

const fetchPlayerName = async (uuid: string): Promise<string | null> => {
  // 1. Check sessionStorage cache
  const sessionCached = getFromSessionCache(uuid);
  if (sessionCached) {
    return sessionCached;
  }

  // 2. Check Supabase backend cache
  const backendResult = await fetchFromBackend(uuid);
  if (backendResult?.name) {
    saveToSessionCache(uuid, backendResult.name);
    return backendResult.name;
  }

  // 3. Fetch from API via Edge Function
  const name = await fetchFromEdgeFunction(uuid);
  
  if (name) {
    saveToSessionCache(uuid, name);
  }
  
  return name;
};

export const usePlayerName = (uuid: string) => {
  const sessionCached = getFromSessionCache(uuid);
  
  return useQuery({
    queryKey: ['playerName', uuid],
    queryFn: () => fetchPlayerName(uuid),
    staleTime: CACHE_EXPIRY_MS,
    gcTime: CACHE_EXPIRY_MS,
    retry: 1,
    refetchOnWindowFocus: false,
    initialData: () => sessionCached ?? undefined,
    // Skip individual fetch when batch system is handling this UUID
    enabled: !isBatchManaged(uuid) || !!sessionCached,
  });
};

// Batch fetch using the optimized batch endpoint
export const useBatchPlayerNames = (uuids: string[]) => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['playerNames', uuids.sort().join(',')],
    queryFn: async () => {
      const results: Record<string, string> = {};
      const uncached: string[] = [];
      
      // Check session cache first
      for (const uuid of uuids) {
        const cached = getFromSessionCache(uuid);
        if (cached) {
          results[uuid] = cached;
          queryClient.setQueryData(['playerName', uuid], cached);
        } else {
          uncached.push(uuid);
        }
      }
      
      if (uncached.length === 0) return results;
      
      // Batch fetch from backend
      try {
        const { data } = await (supabase as any)
          .from('api_gameusers')
          .select('uuid, name, updated_at')
          .in('uuid', uncached);
        
        const sixHoursAgo = new Date(Date.now() - CACHE_EXPIRY_MS);
        const stillMissing: string[] = [];
        
        for (const uuid of uncached) {
          const user = data?.find((u: any) => u.uuid === uuid);
          if (user && user.name && new Date(user.updated_at) > sixHoursAgo) {
            results[uuid] = user.name;
            saveToSessionCache(uuid, user.name);
            queryClient.setQueryData(['playerName', uuid], user.name);
          } else {
            stillMissing.push(uuid);
          }
        }
        
        // Fetch remaining from edge function in batch
        if (stillMissing.length > 0) {
          const { data: edgeData } = await supabase.functions.invoke('gameuser-cache', {
            body: { uuids: stillMissing, size: 16 }
          });
          
          if (edgeData?.results) {
            for (const user of edgeData.results) {
              if (user.name) {
                results[user.uuid] = user.name;
                saveToSessionCache(user.uuid, user.name);
                queryClient.setQueryData(['playerName', user.uuid], user.name);
              }
            }
          }
        }
      } catch (e) {
        console.error('Batch fetch error:', e);
      }
      
      return results;
    },
    staleTime: CACHE_EXPIRY_MS,
    gcTime: CACHE_EXPIRY_MS,
    enabled: uuids.length > 0,
    refetchOnWindowFocus: false,
  });
};

export const useAllPlayerNames = (uuids: string[], enabled: boolean = true) => {
  return useBatchPlayerNames(uuids);
};
